import { CURRENT_NZ_TAX_YEAR } from './currentNzTaxYear';

interface TaxCalculatorInput {
  salary: number;
  period: 'yearly' | 'monthly' | 'weekly';
  kiwiSaverRate: number;
  hasStudentLoan: boolean;
}

interface TaxCalculatorOutput {
  grossPay: number;
  paye: number;
  acc: number;
  kiwiSaver: number;
  studentLoan: number;
  takeHomePay: number;
}

function calculateIncomeTax(annualSalary: number): number {
  let tax = 0;
  let lowerBound = 0;

  for (const bracket of CURRENT_NZ_TAX_YEAR.incomeTax.brackets) {
    if (annualSalary <= lowerBound) {
      break;
    }
    const upperBound = Math.min(annualSalary, bracket.upTo);
    tax += (upperBound - lowerBound) * bracket.rate;
    lowerBound = bracket.upTo;
  }

  return tax;
}

export function calculateTax({
  salary,
  period,
  kiwiSaverRate,
  hasStudentLoan,
}: TaxCalculatorInput): TaxCalculatorOutput {
  // Convert to annual salary if needed
  const annualSalary = period === 'monthly' ? salary * 12 : period === 'weekly' ? salary * 52 : salary;

  const totalTax = calculateIncomeTax(annualSalary);

  // ACC earners' levy applies only up to the maximum liable earnings
  const { rate: accRate, maxLiableEarnings } = CURRENT_NZ_TAX_YEAR.accEarnersLevy;
  const accLevy = Math.min(annualSalary * accRate, maxLiableEarnings * accRate);

  const kiwiSaverAmount = annualSalary * (kiwiSaverRate / 100);

  const { annualRepaymentThreshold, repaymentRate } = CURRENT_NZ_TAX_YEAR.studentLoan;
  let studentLoanRepayment = 0;
  if (hasStudentLoan && annualSalary > annualRepaymentThreshold) {
    studentLoanRepayment = (annualSalary - annualRepaymentThreshold) * repaymentRate;
  }

  const takeHomePay = annualSalary - totalTax - accLevy - kiwiSaverAmount - studentLoanRepayment;

  // Convert results based on period
  const periodMultiplier = period === 'monthly' ? 1 / 12 : period === 'weekly' ? 1 / 52 : 1;

  return {
    grossPay: annualSalary * periodMultiplier,
    paye: totalTax * periodMultiplier,
    acc: accLevy * periodMultiplier,
    kiwiSaver: kiwiSaverAmount * periodMultiplier,
    studentLoan: studentLoanRepayment * periodMultiplier,
    takeHomePay: takeHomePay * periodMultiplier,
  };
}
